import type { DraftWrite } from "@atoms/atom-client";
import type { AtomData, GenericPermission } from "@atoms/atom-types";
import { createContext, type ReactNode } from "react";
import type {
  KeyboardShortcut,
  KeyboardShortcutEvent,
} from "../../lib/keyboardShortcut";

export type LabelType<T> = Record<
  keyof T,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  { label: string; renderer?: (value: any) => ReactNode }
>;

export const MagicFormContext = createContext<FormContextType | undefined>(
  undefined,
);
export const MagicFormProvider = MagicFormContext.Provider;

export type ChangeSet<AtomDataType extends AtomData = AtomData> =
  Partial<AtomDataType>;

export interface SuggestedChange<AtomDataType extends AtomData = AtomData> {
  source?: string;
  explanation?: string;
  changeSet: ChangeSet<AtomDataType>;
}

/**
 * Why a magic form locks its fields, apart from the fields' own `disabled`
 * and enclosing fieldsets. `useMagicFieldDisabledState` reads it to tell the
 * form's lock, a deleted atom and missing permission apart.
 */
export interface MagicFormFieldLock {
  /**
   * Whether the form's own `disabled` locks this root field. False for the
   * form's `alwaysEditableFields`.
   */
  isLockedByForm: (field: string) => boolean;
  /** The user's own permission on a root field, without the form's lock. */
  checkPermission: (field: string, permission: GenericPermission) => boolean;
  /** Shown where the form's `disabled` locks a field. */
  disabledReason?: ReactNode;
  /**
   * Shown where the user may not update a field. Off by default; `true` shows
   * the framework's text.
   */
  permissionDisabledReason?: ReactNode | true;
  /**
   * Shown on the fields of a deleted atom. Off by default; `true` shows the
   * framework's text.
   */
  deletedDisabledReason?: ReactNode | true;
}

/**
 * The labels the form's inputs show, by field path. An input with a `label`
 * prop registers it, so the conflict dialog names a field without an entry in
 * the form's `labels`.
 */
export interface MagicFieldLabelRegistry {
  /** Registers the label of one input. Returns the unregister function. */
  registerFieldLabel: (field: string, label: string) => () => void;
  /** The label of the first mounted input of the field, if any. */
  getFieldLabel: (field: string) => string | undefined;
}

export interface FormContextType<AtomDataType extends AtomData = AtomData> {
  currentValue: AtomDataType;
  /** See `Drafting.updateDraft`: a patch, or a function of the current value. */
  updateDraft: (write: DraftWrite<AtomDataType>) => void;
  setDraft: (draft: AtomDataType) => void;
  removeFromDraft: (keys: (keyof AtomDataType)[]) => void;
  checkFieldPermission: (
    field: string,
    permission: GenericPermission,
  ) => boolean;
  submit: (ignoreSuggestedChanges?: boolean) => Promise<void>;
  loading: boolean;
  draft: Partial<AtomDataType> | undefined;
  /** True while editing, including removal-only drafts. */
  isDirty: boolean;
  valueFromBackend: AtomDataType | undefined;
  resetDraft: () => Promise<void>;
  myUserId: string | undefined;
  suggestedChanges: SuggestedChange<AtomDataType> | undefined;
  setSuggestedChanges: (
    changes: SuggestedChange<AtomDataType> | undefined,
  ) => void;
  atomType: string;
  resolveSuggestedChange: (
    field: keyof AtomDataType,
    value: AtomDataType[keyof AtomDataType],
  ) => void;
  discardSuggestedChanges: () => void;
  onCancel: () => void | Promise<void>;
  labels?: LabelType<AtomDataType>;
  /**
   * The labels of the form's inputs. Optional: a hand-built context may leave
   * it out; the conflict dialog then names fields by `labels` or their name.
   */
  fieldLabels?: MagicFieldLabelRegistry;
  isDeleted: boolean;
  /**
   * Why fields are locked. Optional: a context built without it, such as a
   * hand-built `MagicFormProvider` value, shows only the reasons of the
   * fields' own props and of fieldsets.
   */
  fieldLock?: MagicFormFieldLock;
  registerPreSubmit: (
    id: string,
    callback: () => Promise<Partial<AtomDataType> | undefined>,
    rollback?: () => Promise<void>,
  ) => void;
  unregisterPreSubmit: (id: string) => void;
  registerPostSubmit: (id: string, callback: () => Promise<void>) => void;
  unregisterPostSubmit: (id: string) => void;
  /**
   * The shortcut that saves the form while the focus is inside it, or
   * `undefined` when it has none (`saveShortcut={false}`, or a hand-built
   * context).
   */
  saveShortcut?: KeyboardShortcut;
  /**
   * Whether a key event is the key that saves this form, decided as the form
   * decides it when the event reaches it: its own `saveShortcut` while it
   * has a save button of its own or no group to pass the key to, else the
   * shortcut of the surrounding `MagicFormGroup`. Never while its own
   * shortcut is off. A numeric field commits its pending entry on it. A
   * hand-built context may leave it out; fields then match `saveShortcut`.
   */
  isSaveShortcut?: (event: KeyboardShortcutEvent) => boolean;
  /**
   * Registers a save button for the save shortcut (`MagicSaveButton` does):
   * a function that finds its element when the key is pressed. Returns the
   * unregister function. The shortcut clicks the first registered button
   * that is in the document.
   */
  registerShortcutTarget?: (
    getTarget: () => HTMLElement | null | undefined,
  ) => () => void;
  /**
   * What the form's `validate` reports for the current value; `undefined`
   * while the form is valid. The save buttons are disabled and list them.
   */
  validationErrors?: readonly string[];
}
